import type { ReactNode } from 'react';
import { token } from '@bp-files/ui';
import { FRAME_BUTTON_CLASS } from '@/components/Viewer/ViewerFrame';
import {
  DownloadIcon,
  FitPageIcon,
  FitWidthIcon,
  MinusIcon,
  PlusIcon,
  RotateIcon,
} from './PdfIcons';
import { PdfPager } from './PdfPager';
import { PdfSaveCluster, PdfSignTools, PdfUndoButton } from './PdfSignTools';
import { PDF_VIEWER_COPY } from './pdfViewer.copy';
import type { PdfControls } from './pdfControls';

/**
 * The whole desktop chrome, in the frame's one header (FIL-8b, ADR-0065).
 *
 * **There is no footer at this width, and removing it is the point.** The
 * reader used to spend a full band across the bottom of the screen on a page
 * counter and, while signing, a save — on a document view, where the product of
 * the screen is the page and every horizontal band is page it is not showing.
 *
 * **The bar is five regions across its width, not a run of icons with rules
 * between them.** That was the first attempt, and it read as one undifferentiated
 * list that happened to have lines in it: a divider tells you a boundary exists,
 * where distance tells you what is on each side of it without being read.
 *
 * ```
 * filename    ‹ 1 of 6 ›    − 269% + Fit Rotate    Text Sign    Save    ⤓ ×
 * └ left ┘   └ left-ctr ┘  └────── centre ──────┘ └ right-ctr ┘└──── right ─────────┘
 *   what        where            how it looks       what you do to it      leaving
 * ```
 *
 * The filename is the frame's `<h2>`, a sibling of this component; everything
 * from the pager rightwards is here, spread with `justify-between` rather than
 * packed against one edge. Left to right that is Navigate → View → Edit →
 * Finish, which is the order somebody actually works in.
 *
 * **Sign is the last edit control, after Text.** The two are not peers — a text
 * field is an annotation and a signature is the act the mode exists for — so it
 * sits at the group's trailing edge, closest to Save.
 *
 * **Save is its own region rather than joining the edit group**, and Download
 * stays last. Anywhere earlier, a placed field would shove Download *and* Close
 * rightward the instant it landed, moving Close under the cursor mid-task —
 * which is the worst possible reflow in a modal. Here the only thing that moves
 * is the edit group sliding left, and Close never moves at all.
 *
 * **Download moved from first to last.** It used to lead the toolbar because it
 * was the only control there that did not wait on pdf.js. It still does not wait
 * — see below — but "leave with the file" and "leave" are the same kind of act,
 * and they belong beside each other.
 */
export function PdfToolbarDesktop({
  controls,
  documentReady,
}: {
  controls: PdfControls;
  /** pdf.js has parsed the bytes. Everything but download waits for it. */
  documentReady: boolean;
}) {
  const {
    scale,
    zoomBy,
    fitMode,
    toggleFit,
    rotate,
    drawing,
    onDownload,
    downloadLabel,
    downloadBusy,
  } = controls;

  return (
    <div
      className={`flex w-full flex-wrap items-center gap-x-6 gap-y-2 ${
        // With only Download left to show, `justify-between` would strand it at
        // the *start* of the slot. Nothing to spread means pack right.
        documentReady ? 'justify-between' : 'justify-end'
      }`}
    >
      {documentReady ? (
        <>
          <Region>
            <PdfPager controls={controls} />
          </Region>

          {/*
            **Zoom, fit and rotate are locked while the pencil is armed.** A view
            change mid-drawing would move the page under strokes captured in
            screen pixels — `PdfControls.drawing` has the reasoning — so the whole
            View region greys out until the pencil is put down.
          */}
          <Region>
            <button
              type="button"
              disabled={drawing}
              onClick={() => {
                zoomBy(-1);
              }}
              aria-label={PDF_VIEWER_COPY.zoom.out}
              className={`${FRAME_BUTTON_CLASS} text-secondary hover:text-body disabled:pointer-events-none disabled:opacity-40`}
            >
              <MinusIcon />
            </button>

            <span
              className="min-w-14 text-center text-xs tabular-nums"
              style={{ color: token('bp-text-secondary') }}
            >
              {PDF_VIEWER_COPY.zoom.level(scale)}
            </span>

            <button
              type="button"
              disabled={drawing}
              onClick={() => {
                zoomBy(1);
              }}
              aria-label={PDF_VIEWER_COPY.zoom.in}
              className={`${FRAME_BUTTON_CLASS} text-secondary hover:text-body disabled:pointer-events-none disabled:opacity-40`}
            >
              <PlusIcon />
            </button>

            <button
              type="button"
              disabled={drawing}
              onClick={toggleFit}
              aria-label={
                fitMode === 'width'
                  ? PDF_VIEWER_COPY.zoom.toFitPage
                  : PDF_VIEWER_COPY.zoom.toFitWidth
              }
              className={`${FRAME_BUTTON_CLASS} text-secondary hover:text-body disabled:pointer-events-none disabled:opacity-40`}
            >
              {fitMode === 'width' ? <FitPageIcon /> : <FitWidthIcon />}
            </button>

            <button
              type="button"
              disabled={drawing}
              onClick={rotate}
              aria-label={PDF_VIEWER_COPY.rotate}
              className={`${FRAME_BUTTON_CLASS} text-secondary hover:text-body disabled:pointer-events-none disabled:opacity-40`}
            >
              <RotateIcon />
            </button>
          </Region>

          <Region>
            <PdfSignTools controls={controls} />
            {/*
              Undo sits with the tools that make edits, and appears only once
              there is an edit to take back — including a run of deletes, which
              leaves nothing to save but everything to undo, so it cannot ride
              inside the Save cluster below.
            */}
            {controls.signing.canUndo ? <PdfUndoButton controls={controls} /> : null}
          </Region>

          {/*
            Save appears once there is something to save — a placed field or an
            uncommitted drawing — not whenever signing is available: a button that
            cannot do anything is a question the reader has to answer before they
            can ignore it.
          */}
          {controls.signing.hasContent ? (
            <Region>
              <PdfSaveCluster controls={controls} />
            </Region>
          ) : null}
        </>
      ) : null}

      <Region>
        {/*
          Unlike everything above, this does not wait on `document`: downloading
          does not need the bytes parsed, only fetched, and gating it on pdf.js
          would withhold it while a filing pdf.js cannot open is still perfectly
          downloadable.
        */}
        <button
          type="button"
          disabled={downloadBusy}
          onClick={onDownload}
          aria-label={downloadLabel}
          className={`${FRAME_BUTTON_CLASS} text-secondary hover:text-body`}
        >
          <DownloadIcon />
        </button>
      </Region>
    </div>
  );
}

/**
 * One region of the bar. `gap-2` inside against the row's `gap-x-6` between —
 * a three-to-one ratio is what makes the grouping legible without a rule drawn
 * through it.
 */
function Region({ children }: { children: ReactNode }) {
  return <span className="flex items-center gap-2">{children}</span>;
}
